import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import type { IdentificationDocumentListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { PaperworkValidityBadge } from "web_myplann/Profile/Components/Paperwork/PaperworkValidityBadge";
import {
	getDocumentIcon,
	getDocumentValidity,
	getPaperworkDocumentDisplayName,
} from "web_myplann/Profile/Helpers/paperworkDocument";
import type { PaperworkNavigationState } from "web_myplann/Profile/Types/PaperworkNavigationState";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { useLocationState } from "web_myplann/Shared/Hooks/useLocationState";

type Props = {
	document: IdentificationDocumentListItemResponseDTO;
};

export const PaperworkDocumentRow: FC<Props> = ({ document }) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const navigationState = useLocationState<PaperworkNavigationState>();

	const Icon = getDocumentIcon(document.documentType);
	const { status, formatted } = getDocumentValidity(document.validTo);
	const displayName = getPaperworkDocumentDisplayName(
		document,
		t(Resources.Paperwork.Types[document.documentType]),
	);

	const openDetail = () =>
		navigate(AppRouting.MyPlann.Profile.PaperworkDetail.path(document.id), {
			state: navigationState,
		});

	return (
		<Stack
			component="button"
			type="button"
			direction="row"
			alignItems="center"
			gap={1.4}
			px={1.6}
			py={1.4}
			onClick={openDetail}
			width={"100%"}
			border={"none"}
			bgcolor={"transparent"}
			textAlign={"left"}
			sx={{
				cursor: "pointer",
				"&:active": { bgcolor: theme.palette.grey[50] },
			}}
		>
			<IconBox bg={theme.palette.blue[100]} size={40}>
				<Icon size={20} color={theme.palette.blue[600]} />
			</IconBox>
			<Stack flex={1} minWidth={0}>
				<Typography variant="bodyMedium">{displayName}</Typography>
				{document.number && (
					<Typography
						variant="caption"
						color={theme.palette.grey[500]}
						mt={0.2}
					>
						{t(Resources.Paperwork.numberPrefix)} {document.number}
					</Typography>
				)}
			</Stack>
			<PaperworkValidityBadge status={status} formatted={formatted} />
		</Stack>
	);
};
